import React, { useEffect, useState, useRef, Component } from 'react';
import { MapPin, ArrowRight, CheckCircle2, Info, Star, Lock, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { categories } from '../data/products';
import { ChevronDown } from 'lucide-react';
import { QuoteModal } from './QuoteModal';
// Build a map of category name → { min, max } pricePerYard
const categoryPriceMap: Record<string, {
  min: number;
  max: number;
}> = {};
categories.forEach(cat => {
  const prices = cat.products.map(p => p.pricePerYard);
  categoryPriceMap[cat.name] = {
    min: Math.min(...prices),
    max: Math.max(...prices)
  };
});
// Add bark-specific pricing (Fine Bark, Medium Bark, Coarse Bark are all $45)
const barkProducts = categories.flatMap(cat => cat.products).filter(p => p.name.toLowerCase().includes('bark'));
if (barkProducts.length > 0) {
  const barkPrices = barkProducts.map(p => p.pricePerYard);
  categoryPriceMap['Bark'] = {
    min: Math.min(...barkPrices),
    max: Math.max(...barkPrices)
  };
}
const heroImages = ['https://cdn.magicpatterns.com/uploads/ptMERoj5LvFP4KvLCMA7zo/file_000000003af8720c9523ee9da7df0e47.png', 'https://cdn.magicpatterns.com/uploads/dKFAa8ekbhp3moEHhatGZo/file_000000005a5471f5abe1156df7570912.png', 'https://cdn.magicpatterns.com/uploads/824wZoPcAij7yeGBiywpRR/file_00000000e00c722f85d5870ff5890903.png'];
const CENTER: [number, number] = [47.3087, -122.0032]; // Black Diamond, WA
function getDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
function getZoneInfo(miles: number) {
  if (miles <= 7) return {
    zone: 1,
    fee: 40,
    label: '+$40 Delivery'
  };
  if (miles <= 13) return {
    zone: 2,
    fee: 75,
    label: '+$75 Delivery'
  };
  if (miles <= 18) return {
    zone: 3,
    fee: 120,
    label: '+$120 Delivery'
  };
  if (miles <= 22) return {
    zone: 4,
    fee: 150,
    label: '+$150 Delivery'
  };
  return {
    zone: null,
    fee: 0,
    label: 'Custom Quote'
  };
}
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-500">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2e8a44] focus:border-transparent transition-all flex items-center justify-between ${value ? 'text-white' : 'text-white/50'}`} data-id="element-501">
        <span className="truncate" data-id="element-502">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-503" />
      </button>

      <AnimatePresence data-id="element-504">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full mt-2 bg-stone-900 border border-white/10 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-505">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-white/30 cursor-not-allowed bg-stone-900/50' : value === opt.value ? 'bg-[#2e8a44]/20 text-[#2e8a44]' : 'text-white hover:bg-white/10'}`} data-id="element-506">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Hero() {
  const estimatorRef = useRef<HTMLDivElement>(null);
  const quoteResultRef = useRef<HTMLDivElement>(null);
  const [address, setAddress] = useState('');
  const [material, setMaterial] = useState('Base & Compaction');
  const [quantity, setQuantity] = useState('');
  const [numLoads, setNumLoads] = useState('');
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [quoteResult, setQuoteResult] = useState<{
    priceLow: number;
    priceHigh: number;
    address: string;
    quantity: string;
    material: string;
    deliveryZone: number | null;
    deliveryFee: number;
    deliveryLabel: string;
    smallLoadSurcharge: number;
    effectivePricePerYardLow: number;
    effectivePricePerYardHigh: number;
    basePricePerYardLow: number;
    basePricePerYardHigh: number;
    deliveries?: number;
    bulkDiscount?: number;
  } | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [heroImage] = useState(() => {
    try {
      const key = 'hero_image_index';
      const stored = parseInt(sessionStorage.getItem(key) || '0', 10);
      const index = stored % heroImages.length;
      sessionStorage.setItem(key, String(index + 1));
      return heroImages[index];
    } catch {
      return heroImages[0];
    }
  });
  const [showEstimator, setShowEstimator] = useState(false);
  const [showAddressRow, setShowAddressRow] = useState(false);
  const [hasInteractedWithMaterial, setHasInteractedWithMaterial] = useState(true);
  const [hasInteractedWithQuantity, setHasInteractedWithQuantity] = useState(false);
  // Auto-reveal address row once user has interacted with both fields
  useEffect(() => {
    if (hasInteractedWithMaterial && hasInteractedWithQuantity) {
      if (quantity === 'multi' && !numLoads) {
        setShowAddressRow(false);
        return;
      }
      const timer = setTimeout(() => setShowAddressRow(true), 300);
      return () => clearTimeout(timer);
    }
  }, [hasInteractedWithMaterial, hasInteractedWithQuantity, quantity, numLoads]);
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address) return;
    setIsSearching(true);
    setQuoteResult(null);
    try {
      // Geocode the address
      const query = encodeURIComponent(address + ', WA');
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
      const data = await res.json();
      let zoneInfo = {
        zone: null as number | null,
        fee: 0,
        label: 'Custom Quote'
      };
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        const distance = getDistanceMiles(CENTER[0], CENTER[1], lat, lon);
        zoneInfo = getZoneInfo(distance);
      }
      const priceRange = categoryPriceMap[material] || {
        min: 55,
        max: 55
      };
      const isBark = material === 'Bark';
      const fullLoadYards = isBark ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '3+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (isBark) {
          isSmallLoad = qty < 12;
          surchargeMultiplier = isSmallLoad ? 1 + (12 - qty) * 0.1 : 1;
        } else {
          isSmallLoad = qty < 10;
          surchargeMultiplier = isSmallLoad ? 1 + (10 - qty) * 0.1 : 1;
        }
      }
      // Apply bulk discount: light materials 5% at 15yds, others 5% at 24yds; 10% at 4 full loads
      const bulkPct = qty >= fullLoadYards * 4 ? 0.1 : qty >= (isBark ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      const effectiveLow = Math.ceil(priceRange.min * surchargeMultiplier * (1 - bulkPct));
      const effectiveHigh = Math.ceil(priceRange.max * surchargeMultiplier * (1 - bulkPct));
      const totalLow = effectiveLow * qty + zoneInfo.fee * deliveries;
      const totalHigh = effectiveHigh * qty + zoneInfo.fee * deliveries;
      setQuoteResult({
        priceLow: totalLow,
        priceHigh: totalHigh,
        address,
        quantity: quantity === 'multi' ? String(qty) : quantity,
        material,
        deliveryZone: zoneInfo.zone,
        deliveryFee: zoneInfo.fee * deliveries,
        deliveryLabel: zoneInfo.label,
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effectiveLow,
        effectivePricePerYardHigh: effectiveHigh,
        basePricePerYardLow: priceRange.min,
        basePricePerYardHigh: priceRange.max,
        deliveries,
        bulkDiscount: bulkPct
      });
    } catch {
      const priceRange = categoryPriceMap[material] || {
        min: 55,
        max: 55
      };
      const isBark = material === 'Bark';
      const fullLoadYards = isBark ? 15 : 12;
      let qty = 1;
      let deliveries = 1;
      let isSmallLoad = false;
      let surchargeMultiplier = 1;
      if (quantity === 'multi') {
        if (numLoads === '3+') {
          setIsQuoteModalOpen(true);
          setIsSearching(false);
          return;
        }
        deliveries = parseInt(numLoads) || 2;
        qty = fullLoadYards * deliveries;
      } else {
        qty = parseInt(quantity) || 1;
        if (isBark) {
          isSmallLoad = qty < 12;
          surchargeMultiplier = isSmallLoad ? 1 + (12 - qty) * 0.1 : 1;
        } else {
          isSmallLoad = qty < 10;
          surchargeMultiplier = isSmallLoad ? 1 + (10 - qty) * 0.1 : 1;
        }
      }
      // Apply bulk discount: light materials 5% at 15yds, others 5% at 24yds; 10% at 4 full loads
      const bulkPct2 = qty >= fullLoadYards * 4 ? 0.1 : qty >= (isBark ? fullLoadYards : fullLoadYards * 2) ? 0.05 : 0;
      const effectiveLow = Math.ceil(priceRange.min * surchargeMultiplier * (1 - bulkPct2));
      const effectiveHigh = Math.ceil(priceRange.max * surchargeMultiplier * (1 - bulkPct2));
      const totalLow = effectiveLow * qty + 100 * deliveries;
      const totalHigh = effectiveHigh * qty + 100 * deliveries;
      setQuoteResult({
        priceLow: totalLow,
        priceHigh: totalHigh,
        address,
        quantity: quantity === 'multi' ? String(qty) : quantity,
        material,
        deliveryZone: null,
        deliveryFee: 0,
        deliveryLabel: 'Custom Quote',
        smallLoadSurcharge: isSmallLoad ? Math.round((surchargeMultiplier - 1) * 100) : 0,
        effectivePricePerYardLow: effectiveLow,
        effectivePricePerYardHigh: effectiveHigh,
        basePricePerYardLow: priceRange.min,
        basePricePerYardHigh: priceRange.max,
        deliveries,
        bulkDiscount: bulkPct2
      });
    }
    setIsSearching(false);
    setTimeout(() => {
      if (quoteResultRef.current) {
        const navHeight = document.querySelector('nav')?.offsetHeight || 80;
        const top = quoteResultRef.current.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      }
    }, 250);
  };
  const handleCloseEstimator = () => {
    setShowEstimator(false);
    setShowAddressRow(false);
    setMaterial('Base & Compaction');
    setQuantity('');
    setNumLoads('');
    setHasInteractedWithMaterial(true);
    setHasInteractedWithQuantity(false);
    setQuoteResult(null);
    setAddress('');
  };
  return <section className="relative w-full bg-[#2a2a2a] py-14 md:py-40 overflow-hidden" style={{
    marginTop: '-149px',
    paddingTop: 'calc(149px + 3.5rem)'
  }} data-id="element-507">
      {/* Background Image - extends behind navbar */}
      <motion.div className="absolute inset-0 z-0 bg-cover bg-center" style={{
      backgroundImage: `url(${heroImage})`,
      top: 0
    }} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1.5,
      ease: 'easeInOut'
    }} data-id="element-508" />
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-black/80 via-black/50 to-black/40" data-id="element-509" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-510">
        <div className="text-center md:text-left mb-8 md:mb-12 max-w-3xl" data-id="element-511">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} className="flex items-center justify-center md:justify-start mb-4 md:mb-6" data-id="element-512">
            <span className="text-white/70 text-sm sm:text-base font-medium tracking-wide uppercase" data-id="element-513">
              Trusted by the 253 and beyond
            </span>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.1
        }} data-id="element-514">
            <div className="w-12 md:w-16 h-1 md:h-1.5 bg-[#2da45c] mb-4 md:mb-6 mx-auto md:mx-0 rounded-full" data-id="element-515" />
            <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 md:mb-6 text-white leading-[1.1]" data-id="element-516">
              One Thumb.
              <span className="text-[#2da45c] block mt-1 md:mt-2" data-id="element-517">
                Full Driveway.
              </span>
            </h1>
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.2
        }} className="text-lg md:text-xl text-white/90 font-semibold font-sub mb-5 md:mb-6 max-w-2xl mx-auto md:mx-0" data-id="element-518">
            The smartest way to order gravel, bark &amp; topsoil — instant
            estimates, automatic bulk pricing, and material comparisons, all
            from your phone.
          </motion.p>

          {/* Review Badge + Feature Pills */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.25
        }} className="mb-6 md:mb-10 space-y-4" data-id="element-519">
            <div className="flex items-center gap-2 justify-center md:justify-start" data-id="element-520">
              <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2" data-id="element-521">
                <div className="flex" data-id="element-522">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-523" />)}
                </div>
                <span className="text-white font-bold text-sm ml-1" data-id="element-524">4.9</span>
                <span className="text-white/50 text-sm mx-1" data-id="element-525">·</span>
                <span className="text-white/80 text-sm font-medium" data-id="element-526">
                  200+ deliveries
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-center md:justify-start" data-id="element-527">
              {[{
              icon: '📱',
              label: 'One-Handed Ordering'
            }, {
              icon: '⚡',
              label: 'Instant Estimates'
            }, {
              icon: '🧮',
              label: 'Smart Bulk Discounts'
            }, {
              icon: '📊',
              label: 'Compare Materials'
            }].map(pill => <span key={pill.label} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs sm:text-sm text-white/80 font-medium backdrop-blur-sm" data-id="element-528">
                  <span data-id="element-529">{pill.icon}</span>
                  {pill.label}
                </span>)}
            </div>
          </motion.div>

          {/* CTA Buttons */}
          {!showEstimator && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20,
          delay: 0.3
        }} className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start" data-id="element-530">
              <button onClick={() => {
            const el = document.getElementById('materials');
            if (el) {
              const navHeight = document.querySelector('nav')?.offsetHeight || 80;
              const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
              window.scrollTo({
                top,
                behavior: 'smooth'
              });
            }
          }} className="px-6 py-5 bg-white/10 backdrop-blur-md border border-white/20 text-[#2da45c] hover:bg-white hover:text-stone-900 rounded-xl font-bold text-base sm:text-lg transition-all flex items-center justify-center" data-id="element-531">
                Explore Our Catalog
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-532" />
              </button>
              <button onClick={() => {
            setShowEstimator(true);
            setTimeout(() => {
              if (estimatorRef.current) {
                const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                const top = estimatorRef.current.getBoundingClientRect().top + window.scrollY - navHeight + 8;
                window.scrollTo({
                  top,
                  behavior: 'smooth'
                });
              }
            }, 150);
          }} className="px-6 py-5 bg-brand-button hover:bg-brand-buttonHover text-white rounded-xl font-bold text-base sm:text-lg transition-colors flex items-center justify-center shadow-lg shadow-[#2da45c]/20" data-id="element-533">
                Get Instant Estimate
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-534" />
              </button>
            </motion.div>}
        </div>

        {/* Estimator Card */}
        <AnimatePresence data-id="element-535">
          {showEstimator && <motion.div initial={{
          opacity: 0,
          y: 40,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} ref={estimatorRef} className="relative z-20 bg-black/40 backdrop-blur-xl rounded-2xl border border-white/20 p-6 sm:p-8 mb-10 max-w-3xl mx-auto md:mx-0 shadow-2xl scroll-mt-[125px]" data-id="element-536">
              <button onClick={handleCloseEstimator} className="absolute top-4 right-4 p-2 text-white/60 hover:text-white transition-colors" data-id="element-537">
                <X className="w-5 h-5" data-id="element-538" />
              </button>

              <p className="text-sm font-bold text-brand-muted uppercase tracking-widest mb-5 flex items-center" data-id="element-539">
                <span className="w-8 h-px bg-brand-muted mr-3" data-id="element-540"></span>
                Get an Instant Estimate
              </p>

              <form onSubmit={handleSearch} className="space-y-4" data-id="element-541">
                {/* Row 1: Material + Quantity */}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-542">
                  <CustomSelect value={material} onChange={val => {
                setMaterial(val);
                setHasInteractedWithMaterial(true);
              }} placeholder="Select Material" options={[{
                value: 'Base & Compaction',
                label: 'Base & Compaction'
              }, {
                value: 'Clean Drain Rock',
                label: 'Clean Drain Rock'
              }, {
                value: 'Bark',
                label: 'Bark'
              }, {
                value: 'Soil, Mulch & Org.',
                label: 'Soil & Topsoil'
              }, {
                value: 'Landscape & Decor',
                label: 'Landscape & Decor'
              }, {
                value: 'Quarry Rock',
                label: 'Quarry Rock'
              }]} data-id="element-543" />
                  <CustomSelect value={quantity} onChange={val => {
                setQuantity(val);
                setHasInteractedWithQuantity(true);
                if (val !== 'multi') setNumLoads('');
              }} placeholder="Quantity" options={material === 'Bark' || material === 'Soil, Mulch & Org.' ? [{
                value: '7',
                label: '7 Yards'
              }, {
                value: '8',
                label: '8 Yards'
              }, {
                value: '9',
                label: '9 Yards'
              }, {
                value: '10',
                label: '10 Yards'
              }, {
                value: '11',
                label: '11 Yards'
              }, {
                value: '12',
                label: '12 Yards'
              }, {
                value: '13',
                label: '13 Yards'
              }, {
                value: '14',
                label: '14 Yards'
              }, {
                value: '15',
                label: '15 Yards (Full Load)'
              }, {
                value: 'multi',
                label: 'More than 1 load'
              }] : [{
                value: '6',
                label: '6 Yards'
              }, {
                value: '7',
                label: '7 Yards'
              }, {
                value: '8',
                label: '8 Yards'
              }, {
                value: '9',
                label: '9 Yards'
              }, {
                value: '10',
                label: '10 Yards'
              }, {
                value: '11',
                label: '11 Yards'
              }, {
                value: '12',
                label: '12 Yards (Full Load)'
              }, {
                value: 'multi',
                label: 'More than 1 load'
              }]} data-id="element-544" />
                </motion.div>

                <AnimatePresence data-id="element-545">
                  {quantity === 'multi' && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-visible" data-id="element-546">
                      <div data-id="element-547">
                        <CustomSelect value={numLoads} onChange={val => setNumLoads(val)} placeholder="How many loads?" options={[{
                    value: '2',
                    label: '2 Loads'
                  }, {
                    value: '3',
                    label: '3 Loads'
                  }, {
                    value: '3+',
                    label: '3+ Loads (Custom Quote)'
                  }]} data-id="element-548" />
                      </div>
                      <div className="flex items-start gap-2 p-3 bg-[#2e8a44]/10 border border-[#2e8a44]/20 rounded-lg mt-3" data-id="element-549">
                        <Info className="w-4 h-4 text-[#2e8a44] mt-0.5 shrink-0" data-id="element-550" />
                        <p className="text-xs text-white/90" data-id="element-551">
                          Multi-load orders require separate deliveries. You
                          will be charged a delivery fee per load.
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Row 2: Address + Button */}
                <AnimatePresence data-id="element-552">
                  {showAddressRow && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.3
              }} className="flex flex-col sm:flex-row gap-3 overflow-hidden" data-id="element-553">
                      <div className="relative flex-grow" data-id="element-554">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-555">
                          <MapPin className="h-4 w-4 text-white/60" data-id="element-556" />
                        </div>
                        <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Enter delivery address" autoComplete="street-address" className="block w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#2e8a44] focus:border-transparent transition-all" required data-id="element-557" />
                      </div>
                      <button type="submit" disabled={isSearching} className="bg-brand-button hover:bg-brand-buttonHover text-white px-6 py-3 rounded-lg font-bold text-sm transition-colors flex items-center justify-center sm:w-auto w-full disabled:opacity-70 shrink-0" data-id="element-558">
                        {isSearching ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-559"></div> : <>
                            Get Estimate
                            <ArrowRight className="w-4 h-4 ml-2" data-id="element-560" />
                          </>}
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </form>

              <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-xs text-white/60" data-id="element-561">
                <div className="flex items-center" data-id="element-562">
                  <Lock className="w-3 h-3 mr-1.5 opacity-70" data-id="element-563" />
                  Your information is secure
                </div>
                <span className="hidden sm:inline opacity-30" data-id="element-564">|</span>
                {(() => {
              const now = new Date();
              const pacificOffset = -7; // PDT; use -8 for PST
              const utcHour = now.getUTCHours();
              const pacificHour = (utcHour + pacificOffset + 24) % 24;
              const isActive = pacificHour >= 5 && pacificHour < 18;
              return <div className={`flex items-center gap-1.5 font-medium ${isActive ? 'text-green-400' : 'text-white/30'}`} data-id="element-565">
                      <span className="relative flex h-2 w-2" data-id="element-566">
                        {isActive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-567"></span>}
                        <span className={`relative inline-flex rounded-full h-2 w-2 ${isActive ? 'bg-green-400' : 'bg-white/30'}`} data-id="element-568"></span>
                      </span>
                      {isActive ? 'Dispatch Active' : 'Dispatch Opens 5 AM'}
                    </div>;
            })()}
              </div>

              {/* Quote Result */}
              {quoteResult && <motion.div ref={quoteResultRef} initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-5 pt-5 border-t border-white/10" data-id="element-569">
                  <p className="text-xs text-white/80 font-bold uppercase tracking-wider mb-3" data-id="element-570">
                    Price Breakdown
                  </p>
                  <p className="text-sm text-white/90 font-medium mb-4" data-id="element-571">
                    {quoteResult.quantity} yards of{' '}
                    <span className="font-bold" data-id="element-572">{quoteResult.material}</span>
                    {' → '}
                    {quoteResult.address}
                  </p>

                  {/* Product names in category */}
                  {(() => {
              const mat = quoteResult.material;
              let productNames: string[] = [];
              if (mat === 'Bark') {
                productNames = categories.flatMap(c => c.products).filter(p => p.name.toLowerCase().includes('bark')).map(p => p.name);
              } else {
                const cat = categories.find(c => c.name === mat);
                if (cat) {
                  productNames = cat.products.map(p => p.name);
                }
              }
              if (productNames.length > 0) {
                return <p className="text-xs text-white/50 mb-4 -mt-2" data-id="element-573">
                          Includes: {productNames.join(' · ')}
                        </p>;
              }
              return null;
            })()}

                  {/* Line items */}
                  <div className="space-y-2 mb-4" data-id="element-574">
                    <div className="flex justify-between text-sm" data-id="element-575">
                      <span className="text-white/70" data-id="element-576">
                        Material —{' '}
                        {quoteResult.effectivePricePerYardLow === quoteResult.effectivePricePerYardHigh ? `${quoteResult.effectivePricePerYardLow}/yd` : `${quoteResult.effectivePricePerYardLow}–${quoteResult.effectivePricePerYardHigh}/yd`}{' '}
                        × {quoteResult.quantity} yds
                      </span>
                      <span className="text-white font-semibold" data-id="element-577">
                        {quoteResult.effectivePricePerYardLow === quoteResult.effectivePricePerYardHigh ? `${quoteResult.effectivePricePerYardLow * parseInt(quoteResult.quantity)}` : `${quoteResult.effectivePricePerYardLow * parseInt(quoteResult.quantity)}–${quoteResult.effectivePricePerYardHigh * parseInt(quoteResult.quantity)}`}
                      </span>
                    </div>
                    {quoteResult.smallLoadSurcharge > 0 && <p className="text-xs text-[#2e8a44] -mt-1 ml-0.5" data-id="element-578">
                        Includes small load upcharge (
                        {quoteResult.basePricePerYardLow === quoteResult.basePricePerYardHigh ? `${quoteResult.basePricePerYardLow}/yd` : `${quoteResult.basePricePerYardLow}–${quoteResult.basePricePerYardHigh}/yd`}{' '}
                        at 10+ yards)
                      </p>}
                    {quoteResult.bulkDiscount && quoteResult.bulkDiscount > 0 ? <p className="text-xs text-[#2e8a44] -mt-1 ml-0.5" data-id="element-579">
                        {Math.round(quoteResult.bulkDiscount * 100)}% bulk
                        discount applied ({parseInt(quoteResult.quantity)}+
                        yards)
                      </p> : null}
                    <div className="flex justify-between text-sm" data-id="element-580">
                      <span className="text-white/70" data-id="element-581">
                        Delivery —{' '}
                        {quoteResult.deliveryZone !== null ? `Zone ${quoteResult.deliveryZone}` : 'Custom Quote'}
                        {quoteResult.deliveries && quoteResult.deliveries > 1 ? ` (×${quoteResult.deliveries})` : ''}
                      </span>
                      <span className="text-white font-semibold" data-id="element-582">
                        {quoteResult.deliveryZone !== null ? `${quoteResult.deliveryFee}` : 'TBD'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-white/10" data-id="element-583">
                      <span className="text-white font-bold text-sm" data-id="element-584">
                        Estimated Total
                      </span>
                      <span className="text-2xl font-heading font-extrabold text-white" data-id="element-585">
                        {quoteResult.priceLow === quoteResult.priceHigh ? `${quoteResult.priceLow}` : `${quoteResult.priceLow}–${quoteResult.priceHigh}`}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-center gap-3" data-id="element-586">
                    <a href="tel:206-619-3742" className="bg-white hover:bg-stone-200 text-stone-900 px-6 py-2.5 rounded-lg font-bold text-sm transition-colors" data-id="element-587">
                      Call to Order
                    </a>
                    <a href={`/?cat=${encodeURIComponent(material)}#materials`} onClick={e => {
                e.preventDefault();
                // Map hero material names to actual category names
                const materialToCat: Record<string, string> = {
                  Bark: 'Soil, Mulch & Org.'
                };
                const selectedMaterial = material;
                const catName = materialToCat[selectedMaterial] || selectedMaterial;
                handleCloseEstimator();
                // Navigate to the materials section with the selected category expanded
                const params = new URLSearchParams({
                  cat: catName
                });
                window.history.replaceState({}, '', `/?${params.toString()}`);
                window.dispatchEvent(new Event('materialsNav'));
                // Scroll to materials section
                setTimeout(() => {
                  const el = document.getElementById('materials');
                  if (el) {
                    const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                    const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
                    window.scrollTo({
                      top,
                      behavior: 'smooth'
                    });
                  }
                }, 150);
              }} className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-6 py-2.5 rounded-lg font-bold text-sm transition-colors" data-id="element-588">
                      Dig Deeper
                    </a>
                  </div>

                  {/* Delivery Zone Note */}
                  {quoteResult.deliveryZone !== null ? <p className="mt-4 text-xs text-white/70 leading-relaxed text-center font-medium" data-id="element-589">
                      Delivery fee based on your{' '}
                      <Link to={`/delivery-zones?address=${encodeURIComponent(address)}`} className="text-[#2e8a44] hover:underline" data-id="element-590">
                        delivery zone
                      </Link>
                      .
                    </p> : <div className="mt-3 flex items-center justify-center text-xs text-[#2e8a44]" data-id="element-591">
                      <Info className="w-3.5 h-3.5 mr-1.5 shrink-0" data-id="element-592" />
                      Your address is outside standard zones. Delivery fee may
                      apply —{' '}
                      <Link to={`/delivery-zones?address=${encodeURIComponent(address)}`} className="font-semibold ml-1 hover:underline" data-id="element-593">
                        check delivery zones
                      </Link>
                    </div>}

                  {/* Discreet reset */}
                  <div className="mt-4 flex justify-end" data-id="element-594">
                    <button onClick={() => {
                handleCloseEstimator();
                setShowEstimator(true);
                setTimeout(() => {
                  if (estimatorRef.current) {
                    const navHeight = document.querySelector('nav')?.offsetHeight || 80;
                    const top = estimatorRef.current.getBoundingClientRect().top + window.scrollY - navHeight + 8;
                    window.scrollTo({
                      top,
                      behavior: 'smooth'
                    });
                  }
                }, 150);
              }} className="text-[10px] text-white/40 hover:text-white/70 transition-colors" data-id="element-595">
                      Start over ↻
                    </button>
                  </div>
                </motion.div>}
            </motion.div>}
        </AnimatePresence>

        {/* Trust Badges */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: 0.4
      }} className="flex flex-wrap justify-center md:justify-start gap-3 mt-8" data-id="element-596">
          <div className="flex items-center px-4 py-2 text-sm text-white font-medium" data-id="element-597">
            <CheckCircle2 className="w-4 h-4 mr-2 text-[#2e8a44]" data-id="element-598" />
            Order from your phone in minutes
          </div>
          <div className="flex items-center px-4 py-2 text-sm text-white font-medium" data-id="element-599">
            <CheckCircle2 className="w-4 h-4 mr-2 text-[#2e8a44]" data-id="element-600" />
            No hidden fees
          </div>
          <div className="flex items-center px-4 py-2 text-sm text-white font-medium" data-id="element-601">
            <CheckCircle2 className="w-4 h-4 mr-2 text-[#2e8a44]" data-id="element-602" />
            Pay on delivery
          </div>
        </motion.div>
      </div>
      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-603" />
    </section>;
}